import { useMemo, useState } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useAppContext } from "@context/state";
import { useAbility } from "@hooks/useAbility";
import { ABILITY } from "@constants/abilities";
import { formatDollarAmount } from "@utils/costFormatter";
import { getSaiMeaning, getSaiValues } from "@utils/homeDashboard";
import SaiCard from "./SaiCard";
import InsightCard from "./InsightCard";
import SaiExplainerDrawer from "./SaiExplainerDrawer";

// TODO(#1221): Likely/Unlikely pills are hidden until the client decides on the logic.
// Set these to "Likely"/"Unlikely" to show them again.
const FEDERAL_STATUS = null;
const INSTITUTIONAL_STATUS = null;

const SaiSection = () => {
  const { scenario } = useAppContext();
  const { isHidden } = useAbility();
  const [isExplainerOpen, setIsExplainerOpen] = useState(false);

  const sai = useMemo(() => getSaiValues(scenario?.efcs), [scenario?.efcs]);
  const meaning = useMemo(() => getSaiMeaning(sai), [sai]);

  const showFederal = !isHidden(ABILITY.HOME_FEDERAL_SAI);
  const showInstitutional = !isHidden(ABILITY.HOME_INSTITUTIONAL_SAI);

  if (!showFederal && !showInstitutional) {
    return null;
  }

  const institutionalRange = sai.isRange
    ? `falls between ${formatDollarAmount(sai.institutionalLower)} and ${formatDollarAmount(sai.institutionalUpper)}`
    : `is about ${formatDollarAmount(sai.institutionalLower)}`;

  return (
    <section aria-labelledby="sai-heading" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="sai-heading" className="m-0 text-xl font-bold text-gray-900">
          Your Student Aid Index (SAI)
        </h2>
        <button
          type="button"
          onClick={() => setIsExplainerOpen(true)}
          aria-haspopup="dialog"
          className="flex items-center text-sm font-medium text-primary-500 hover:underline rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
        >
          What is an SAI, and how is it calculated?
          <ChevronRightIcon fontSize="small" aria-hidden="true" />
        </button>
      </div>

      <div
        className={`grid gap-3 grid-cols-1 ${showFederal && showInstitutional ? "md:grid-cols-2" : ""}`}
      >
        {showFederal && (
          <SaiCard
            title="Federal SAI"
            source="From the FAFSA"
            value={sai.federalLabel}
            impact="Used to determine eligibility for federal aid and to calculate institutional grants at many colleges."
            status={FEDERAL_STATUS}
            tooltipTitle="In-state public colleges"
            tooltipBody={`Public colleges and the federal government use this number to decide your aid, including federal grants and loans. Many in-state public colleges cost about ${formatDollarAmount(sai.federal)} a year, so you'd likely pay close to the price tag unless you earn merit scholarships.`}
          />
        )}
        {showInstitutional && (
          <SaiCard
            title="Institutional SAI"
            source="From the CSS Profile"
            value={sai.institutionalLabel}
            impact="A more in-depth formula used by about 200 colleges, mostly private, to distribute institutional aid."
            status={INSTITUTIONAL_STATUS}
            tooltipTitle="Private colleges that cover full need"
            tooltipBody={`Many private colleges use this number to decide how much of their own aid to give you. Each college calculates it a little differently, so yours ${institutionalRange}. If a college covers your full need, you’d pay about that much a year, even if its price tag is much higher.`}
          />
        )}
      </div>

      {showFederal && showInstitutional && (
        <InsightCard label="What your SAIs mean" body={meaning} />
      )}

      <SaiExplainerDrawer
        open={isExplainerOpen}
        onClose={() => setIsExplainerOpen(false)}
      />
    </section>
  );
};

export default SaiSection;
